import React, { useState } from 'react';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { addDayProblem } from '../lib/holidayActions';
import { placeKey, placeLabel, parsePlace } from '../lib/holidayPlaces';

/**
 * "Add a day your company takes off" — one form, both screens.
 *
 * ⚠️ IT ASKS WHO GETS THE DAY, AND ROB IS THE REASON (13/08/2026). I offered him "always
 * everyone" or "always the company's country"; he took neither, and his case is better
 * than both: *"If you are adding a day because it does not show in their religious
 * calendar for example in Algeria, you would not want it to be a day off in UK, however if
 * you were entering a day which was like a founders day or company gathering day you would
 * want it for all locations."*
 *
 * ⚠️ THE PICKER ONLY APPEARS FOR A COMPANY WITH MORE THAN ONE PLACE. Most customers have
 * one office in one country, where "who gets it?" has exactly one answer — and a question
 * with one answer teaches people to click past questions.
 *
 * ⚠️ "EVERYONE" IS NO COUNTRY AND NO REGION, which is not a new convention: the server's
 * `holiday_applies` has always compared country only when both sides have one, and always
 * read an absent region as "everywhere in this country". A row with neither reaches
 * everybody.
 *
 * ⚠️ AND THE DEFAULT IS EVERYONE ON PURPOSE. The wrong-way-round failure matters: a
 * founders' day that reaches too few people is a complaint on the day itself, while one
 * that reaches too many is at worst a day off somebody wasn't expecting. This is also the
 * ONLY value that cannot be a silent mistake, because it is what "company day" says.
 */
const AddCompanyDayForm = ({
  places = [],
  countryNames = {},
  onAdd,
  onCancel,
  compact = false,
}) => {
  const [form, setForm] = useState({ name: '', date: '', place: '' });
  const [saving, setSaving] = useState(false);

  const multiCountry = new Set(
    places.map((p) => p.countryCode).filter(Boolean)).size > 1;

  const submit = async (e) => {
    e.preventDefault();
    const problem = addDayProblem(form);
    if (problem) { setForm({ ...form }); onAdd({ problem }); return; }
    const { countryCode, region } = form.place ? parsePlace(form.place)
      : { countryCode: null, region: null };
    setSaving(true);
    try {
      // ⚠️ `null`, NOT `''`. An empty string is a country code the server would try to
      // match, and nothing would ever match it — the row would be invisible to everybody,
      // which is a nastier version of the bug this whole change is fixing.
      const ok = await onAdd({ name: form.name.trim(), date: form.date,
        country_code: countryCode || null, region: region || null });
      if (ok !== false) setForm({ name: '', date: '', place: '' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className={compact ? 'space-y-2' : 'flex flex-wrap gap-4 items-end'}>
      <div className={compact ? '' : 'flex-1 min-w-48'}>
        {!compact && <label className="block text-sm font-medium mb-2">Holiday name</label>}
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Founders' day"
          data-testid="holiday-name-input"
        />
      </div>
      <div className={compact ? 'flex flex-wrap gap-2' : 'contents'}>
        <div>
          {!compact && <label className="block text-sm font-medium mb-2">Date</label>}
          <Input
            type="date"
            className={compact ? 'w-44' : undefined}
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="holiday-date-input"
          />
        </div>
        {places.length > 1 && (
          <div>
            {!compact && <label className="block text-sm font-medium mb-2">Who gets this day off?</label>}
            <select
              value={form.place}
              onChange={(e) => setForm({ ...form, place: e.target.value })}
              className="input-hive h-10"
              data-testid="holiday-place-select"
              aria-label="Who gets this day off?"
            >
              <option value="">Everyone</option>
              {places.map((p) => (
                <option key={p.key || placeKey(p.countryCode, p.region)}
                  value={p.key || placeKey(p.countryCode, p.region)}>
                  {placeLabel(p, countryNames, multiCountry)} only
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex gap-2">
          <Button type="submit" size={compact ? 'sm' : undefined}
            variant={compact ? 'outline' : undefined}
            className={compact ? undefined : 'btn-primary'}
            disabled={saving}
            data-testid="save-holiday-btn">
            {saving ? 'Adding…' : 'Add'}
          </Button>
          {onCancel && (
            <Button type="button" variant="outline" size={compact ? 'sm' : undefined}
              onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </div>
      {places.length > 1 && (
        <p className={`text-xs text-muted-foreground ${compact ? '' : 'w-full'}`}>
          A founders' day or a shutdown is usually for everyone. A local observance
          belongs to the place that observes it.
        </p>
      )}
    </form>
  );
};

export default AddCompanyDayForm;
